// Single source of truth for the markets StringsSG operates in.
//
// Singapore predates this file and most SG pages still hardcode their own
// country/currency props - the `sg` entry here exists so shared helpers
// (formatMoney, market-aware schema builders, the nav switcher) have one shape
// to read, NOT because the SG pages were migrated onto it. Do not "clean up"
// the SG pages to read from here without diffing their HTML/JSON-LD first.
//
// UAE and Malaysia launched SEO-first (Oct 2026). `paymentsEnabled` decides
// whether a country's requests get the Singapore payment step (Pay Now / Pay
// Later / No Card, via Stripe in the request's currency). Singapore always has
// it; other countries have it where REACT_APP_PAYMENTS_MARKETS lists their code
// (comma-separated, e.g. "ae,my") - set in UAT, unset in production until the
// payout model is decided. Without it the customer confirms the request and
// our team arranges payment before the visit.
//
// `features` are per-country launch switches, all on for Singapore:
//  - trainerMatch: the PT booking runs the trainer match step and the
//    trainer sign-in (PtTrainerMatchLoader -> PtQuickSignIn). Off: the plain
//    QuickSignIn submits a request and ops matches a trainer by hand. Turn on
//    once the country has trainers on the platform.
//  - reviews: customer reviews / success stories sections. On with no reviews
//    for the country falls back to Singapore's (lib/market-pages.js).
//  - trainerRoster: trainer strip and carousels. Never falls back - Singapore
//    trainers cannot train in another country.
//  - googleRating: the Google rating pill. Uses `gbp` when the market has its
//    own Business Profile, otherwise Singapore's.
// Read them with hasFeature(market, key).
//
// Terminology is deliberately per market. "Aircon" is Singapore usage: UAE
// pages say "AC", Malaysian pages say "aircond" (with "aircon" allowed only as a
// secondary variant in body copy). Use t(market, key) for every user-visible
// service term on AE/MY surfaces.

const PAYMENTS_MARKETS = (process.env.REACT_APP_PAYMENTS_MARKETS || '')
    .split(',')
    .map((cd) => cd.trim().toLowerCase())
    .filter(Boolean)
const paymentsOn = (countryCd) => PAYMENTS_MARKETS.includes(countryCd)

export const MARKETS = {
    sg: {
        countryCd: 'sg',
        // Trainer search radius for the booking flow's match step.
        ptMatchRadiusKm: 50,
        // Pay Later (5%) and No Card (15%) surcharge caps, in local currency.
        paymentSurchargeCaps: { payLater: 15, noCard: 25 },
        // Tall-ladder charge for high ceilings, agreed at confirmation (local currency).
        highCeilingSurcharge: 50,
        // Address hints in the app's confirmation sheets.
        addressPlaceholder: '10 Raffles City, Unit 01-07, Singapore 123456',
        normalCeilingHomes: 'Most HDB and Condominiums',
        countryName: 'Singapore',
        shortName: 'Singapore',
        currency: 'SGD',
        currencyPrefix: '$',
        locale: 'en-SG',
        hreflang: 'en-sg',
        timeZone: 'Asia/Singapore',
        utcOffset: '+08:00',
        telCd: '+65',
        distanceUnit: 'kms',
        addressMode: 'sg-postal-onemap',
        paymentsEnabled: true,
        paymentMethods: ['card', 'paynow'],
        features: { trainerMatch: true, reviews: true, trainerRoster: true, googleRating: true },
        homePath: '/sg',
        flagEmoji: '🇸🇬',
        centre: { lat: 1.3521, lng: 103.8198 },
        verticals: ['aircon', 'pt', 'handyman', 'cleaner', 'electrician', 'plumber', 'property', 'finance'],
        hubs: {
            aircon: { singapore: '/sg/aircon-servicing-singapore' },
            pt: { singapore: '/sg/personal-trainer' },
        },
        cities: [],
        terms: {
            acCategory: 'Aircon Servicing',
            acShort: 'aircon',
            acUnit: 'aircon unit',
            acGeneral: 'General Servicing',
            acChemicalWash: 'Chemical Wash',
            acChemicalOverhaul: 'Chemical Overhaul',
            acGas: 'Gas Top-Up',
            acAnnual: 'Annual Package',
            acRepair: 'Aircon Repair',
            acNotCooling: 'Aircon Not Cold',
            acLeaking: 'Aircon Leaking Water',
            mountWall: 'Wall',
            mountCeiling: 'Ceiling',
            mountDucted: 'Ducted',
            capacityUnit: null,
            homes: 'HDB, condo or landed home',
            coverage: 'island-wide',
            utility: 'SP Group',
            ptBuddy: 'Buddy Training',
            ptPostnatal: 'postpartum',
            ptOlder: 'elderly',
        },
    },

    ae: {
        countryCd: 'ae',
        // Trainer search radius for the booking flow's match step.
        ptMatchRadiusKm: 30,
        // Pay Later (5%) and No Card (15%) surcharge caps, in local currency.
        paymentSurchargeCaps: { payLater: 40, noCard: 70 },
        // Scaled from Singapore's $50 like the service prices.
        highCeilingSurcharge: 125,
        addressPlaceholder: 'Apartment 1204, Marina Heights Tower, Dubai Marina',
        normalCeilingHomes: 'Most apartments and townhouses',
        countryName: 'United Arab Emirates',
        shortName: 'UAE',
        currency: 'AED',
        currencyPrefix: 'AED ',
        locale: 'en-AE',
        hreflang: 'en-ae',
        timeZone: 'Asia/Dubai',
        utcOffset: '+04:00',
        telCd: '+971',
        distanceUnit: 'kms',
        // The UAE has no usable postcodes: Google Places for the community,
        // then building/villa and apartment number by hand.
        addressMode: 'places-no-postcode',
        paymentsEnabled: paymentsOn('ae'),
        paymentMethods: ['card'],
        features: { trainerMatch: false, reviews: false, trainerRoster: false, googleRating: false },
        homePath: '/ae',
        flagEmoji: '🇦🇪',
        centre: { lat: 25.2048, lng: 55.2708 },
        verticals: ['aircon', 'pt'],
        phonePlaceholder: '50 123 4567',
        // UAE mobiles: 05x xxx xxxx locally, +971 5x xxx xxxx internationally.
        phonePattern: '(?:\\+?971[\\s-]?|0)?5\\d[\\s-]?\\d{3}[\\s-]?\\d{4}',
        hubs: {
            aircon: {
                dubai: '/ae/ac-service-dubai',
                'abu-dhabi': '/ae/ac-service-abu-dhabi',
            },
            pt: {
                dubai: '/ae/personal-trainer-dubai',
                'abu-dhabi': '/ae/personal-trainer-abu-dhabi',
            },
        },
        cities: [
            {
                slug: 'dubai',
                name: 'Dubai',
                lat: 25.2048,
                lng: 55.2708,
                utility: 'DEWA',
                localities: [
                    { slug: 'dubai-marina', name: 'Dubai Marina', lat: 25.0805, lng: 55.1403 },
                    { slug: 'jlt', name: 'Jumeirah Lake Towers (JLT)', lat: 25.0693, lng: 55.1413 },
                    { slug: 'jvc', name: 'Jumeirah Village Circle (JVC)', lat: 25.0603, lng: 55.2094 },
                    { slug: 'downtown-dubai', name: 'Downtown Dubai', lat: 25.1972, lng: 55.2744 },
                    { slug: 'business-bay', name: 'Business Bay', lat: 25.1850, lng: 55.2650 },
                    { slug: 'palm-jumeirah', name: 'Palm Jumeirah', lat: 25.1124, lng: 55.1390 },
                    { slug: 'jumeirah', name: 'Jumeirah', lat: 25.2100, lng: 55.2520 },
                    { slug: 'al-barsha', name: 'Al Barsha', lat: 25.1110, lng: 55.1960 },
                    { slug: 'dubai-hills-estate', name: 'Dubai Hills Estate', lat: 25.1090, lng: 55.2450 },
                    { slug: 'arabian-ranches', name: 'Arabian Ranches', lat: 25.0550, lng: 55.2690 },
                    { slug: 'mirdif', name: 'Mirdif', lat: 25.2200, lng: 55.4200 },
                    { slug: 'deira', name: 'Deira', lat: 25.2700, lng: 55.3200 },
                    { slug: 'bur-dubai', name: 'Bur Dubai', lat: 25.2550, lng: 55.2950 },
                    { slug: 'al-karama', name: 'Al Karama', lat: 25.2440, lng: 55.3040 },
                    { slug: 'dubai-silicon-oasis', name: 'Dubai Silicon Oasis', lat: 25.1190, lng: 55.3790 },
                    { slug: 'international-city', name: 'International City', lat: 25.1650, lng: 55.4090 },
                    { slug: 'motor-city', name: 'Motor City', lat: 25.0450, lng: 55.2390 },
                    { slug: 'discovery-gardens', name: 'Discovery Gardens', lat: 25.0390, lng: 55.1450 },
                ],
            },
            {
                slug: 'abu-dhabi',
                name: 'Abu Dhabi',
                lat: 24.4539,
                lng: 54.3773,
                utility: 'TAQA (ADDC)',
                localities: [
                    { slug: 'al-reem-island', name: 'Al Reem Island', lat: 24.4990, lng: 54.4060 },
                    { slug: 'khalifa-city', name: 'Khalifa City', lat: 24.4190, lng: 54.5780 },
                    { slug: 'al-raha-beach', name: 'Al Raha Beach', lat: 24.4560, lng: 54.6100 },
                    { slug: 'yas-island', name: 'Yas Island', lat: 24.4880, lng: 54.6080 },
                    { slug: 'saadiyat-island', name: 'Saadiyat Island', lat: 24.5450, lng: 54.4330 },
                    { slug: 'corniche', name: 'Corniche', lat: 24.4700, lng: 54.3350 },
                    { slug: 'al-reef', name: 'Al Reef', lat: 24.4620, lng: 54.6810 },
                    { slug: 'mohammed-bin-zayed-city', name: 'Mohammed Bin Zayed City', lat: 24.3480, lng: 54.5470 },
                    { slug: 'al-khalidiyah', name: 'Al Khalidiyah', lat: 24.4720, lng: 54.3520 },
                    { slug: 'al-mushrif', name: 'Al Mushrif', lat: 24.4380, lng: 54.3890 },
                    { slug: 'masdar-city', name: 'Masdar City', lat: 24.4270, lng: 54.6160 },
                ],
            },
        ],
        terms: {
            acCategory: 'AC Service',
            acShort: 'AC',
            acUnit: 'AC unit',
            acGeneral: 'AC Cleaning',
            acChemicalWash: 'AC Coil Cleaning (Chemical Wash)',
            acChemicalOverhaul: 'AC Deep Cleaning (Full Dismantle)',
            acGas: 'AC Gas Refill',
            acAnnual: 'AC Maintenance Contract (AMC)',
            acRepair: 'AC Repair',
            acNotCooling: 'AC Not Cooling',
            acLeaking: 'AC Water Leaking',
            mountWall: 'Split (wall)',
            mountCeiling: 'Ceiling cassette',
            mountDucted: 'Ducted (concealed)',
            capacityUnit: 'ton',
            homes: 'apartment, villa or townhouse',
            coverage: 'across the city',
            utility: 'DEWA',
            ptBuddy: 'Couple / Partner Training',
            ptPostnatal: 'postnatal',
            ptOlder: 'seniors',
        },
    },

    my: {
        countryCd: 'my',
        // Trainer search radius for the booking flow's match step.
        ptMatchRadiusKm: 25,
        // Pay Later (5%) and No Card (15%) surcharge caps, in local currency.
        paymentSurchargeCaps: { payLater: 45, noCard: 75 },
        // Scaled from Singapore's $50 like the service prices.
        highCeilingSurcharge: 95,
        addressPlaceholder: 'A-12-3, Residensi Kiara, Jalan Kiara 3, 50480 Kuala Lumpur',
        normalCeilingHomes: 'Most condos and apartments',
        countryName: 'Malaysia',
        shortName: 'Malaysia',
        currency: 'MYR',
        currencyPrefix: 'RM',
        locale: 'en-MY',
        hreflang: 'en-my',
        timeZone: 'Asia/Kuala_Lumpur',
        utcOffset: '+08:00',
        telCd: '+60',
        distanceUnit: 'kms',
        addressMode: 'places-postcode',
        paymentsEnabled: paymentsOn('my'),
        // Cards and wallets through Stripe; FPX / GrabPay need a Malaysian Stripe account.
        paymentMethods: ['card'],
        features: { trainerMatch: false, reviews: false, trainerRoster: false, googleRating: false },
        homePath: '/my',
        flagEmoji: '🇲🇾',
        centre: { lat: 3.1390, lng: 101.6869 },
        verticals: ['aircon', 'pt'],
        phonePlaceholder: '12-345 6789',
        // Malaysian mobiles: 01x-xxx xxxx (some 01x-xxxx xxxx).
        phonePattern: '(?:\\+?60[\\s-]?|0)?1\\d[\\s-]?\\d{3,4}[\\s-]?\\d{4}',
        hubs: {
            aircon: { 'kuala-lumpur': '/my/aircond-service-kuala-lumpur' },
            pt: { 'kuala-lumpur': '/my/personal-trainer-kuala-lumpur' },
        },
        cities: [
            {
                slug: 'kuala-lumpur',
                name: 'Kuala Lumpur',
                shortName: 'KL',
                lat: 3.1390,
                lng: 101.6869,
                utility: 'TNB',
                localities: [
                    { slug: 'mont-kiara', name: 'Mont Kiara', lat: 3.1710, lng: 101.6510 },
                    { slug: 'bangsar', name: 'Bangsar', lat: 3.1300, lng: 101.6780 },
                    { slug: 'klcc', name: 'KLCC', lat: 3.1579, lng: 101.7120 },
                    { slug: 'bukit-bintang', name: 'Bukit Bintang', lat: 3.1466, lng: 101.7100 },
                    { slug: 'cheras', name: 'Cheras', lat: 3.0950, lng: 101.7450 },
                    { slug: 'setapak', name: 'Setapak', lat: 3.1960, lng: 101.7180 },
                    { slug: 'kepong', name: 'Kepong', lat: 3.2130, lng: 101.6370 },
                    { slug: 'sri-petaling', name: 'Sri Petaling', lat: 3.0690, lng: 101.6870 },
                    { slug: 'desa-parkcity', name: 'Desa ParkCity', lat: 3.1860, lng: 101.6300 },
                    { slug: 'ampang', name: 'Ampang', lat: 3.1500, lng: 101.7600 },
                    { slug: 'sentul', name: 'Sentul', lat: 3.1850, lng: 101.6920 },
                    { slug: 'ttdi', name: 'Taman Tun Dr Ismail (TTDI)', lat: 3.1470, lng: 101.6280 },
                    { slug: 'damansara-heights', name: 'Damansara Heights', lat: 3.1490, lng: 101.6610 },
                    { slug: 'brickfields', name: 'Brickfields', lat: 3.1290, lng: 101.6850 },
                    { slug: 'wangsa-maju', name: 'Wangsa Maju', lat: 3.2050, lng: 101.7370 },
                    { slug: 'old-klang-road', name: 'Old Klang Road', lat: 3.0950, lng: 101.6720 },
                    { slug: 'petaling-jaya', name: 'Petaling Jaya', lat: 3.1073, lng: 101.6067, state: 'Selangor' },
                    { slug: 'subang-jaya', name: 'Subang Jaya', lat: 3.0565, lng: 101.5851, state: 'Selangor' },
                ],
            },
        ],
        terms: {
            acCategory: 'Aircond Service',
            acShort: 'aircond',
            acUnit: 'aircond unit',
            acGeneral: 'Aircond General Service',
            acChemicalWash: 'Chemical Wash',
            acChemicalOverhaul: 'Chemical Overhaul',
            acGas: 'Gas Top Up',
            acAnnual: 'Aircond Servicing Package',
            acRepair: 'Aircond Repair',
            acNotCooling: 'Aircond Not Cold',
            acLeaking: 'Aircond Leaking Water',
            mountWall: 'Wall-mounted',
            mountCeiling: 'Ceiling cassette',
            mountDucted: 'Ducted',
            capacityUnit: 'hp',
            homes: 'condo, apartment or landed home',
            coverage: 'across KL and the Klang Valley',
            utility: 'TNB',
            ptBuddy: 'Partner Training',
            ptPostnatal: 'postnatal',
            ptOlder: 'elderly',
        },
    },
}

// Markets with public pages built from this registry (SG has its own pages).
export const EXPANSION_MARKETS = ['ae', 'my']

export const getMarket = (countryCd) =>
    MARKETS[(countryCd || '').toLowerCase()] ?? MARKETS.sg

export const getMarketByName = (countryName) =>
    Object.values(MARKETS).find((m) => m.countryName === countryName) ?? null

// '/ae/...' -> ae, '/my/...' -> my, '/sg/...' -> sg; everything else -> null
// (caller decides, usually from LocationContext). '/' is the global brand page
// and belongs to no country - callers that need one there fall back to
// LocationContext, which defaults to Singapore.
export const getMarketFromPath = (pathname) => {
    const first = (pathname || '').split('/').filter(Boolean)[0]
    return first && MARKETS[first] ? MARKETS[first] : null
}

export const getCity = (countryCd, citySlug) =>
    getMarket(countryCd).cities.find((c) => c.slug === citySlug) ?? null

export const getLocality = (countryCd, citySlug, localitySlug) =>
    getCity(countryCd, citySlug)?.localities.find((l) => l.slug === localitySlug) ?? null

// Terminology lookup. Falls back to the SG term so a missing key degrades to
// readable (if wrong-dialect) text rather than "undefined" on the page.
export const t = (market, key) =>
    (market?.terms ?? MARKETS.sg.terms)[key] ?? MARKETS.sg.terms[key] ?? key

// Swaps Singapore's "aircon" for the market's term ("AC", "aircond") in SG
// copy. Singapore (or no market) gets the text back unchanged. Only for
// surfaces crawlers never see - booking sheets and modals. Page sections take
// their own market copy instead, or the AE/MY pages would carry the SG pages'
// wording word for word (see the plan's anti-cannibalisation rules).
export const localiseTerms = (text, market) => {
    if (typeof text !== 'string' || !market || market.countryCd === 'sg') return text
    const short = t(market, 'acShort')
    // "aircon" or "aircons" (the plural keeps its s: "ACs", "airconds").
    return text
        .replace(/\bAIRCON(?=S?\b)/g, short.toUpperCase())
        .replace(/\bAircon(?=s?\b)/g, capitalise(short))
        .replace(/\baircon(?=s?\b)/g, short)
}

// Where aircon booking starts for a country. Aircon never uses the question
// wizard: every entry point goes to a page whose buttons open the
// AirconItemSelection picker (or quick-book a priced tile). Logged-in users
// use /home/services/aircon instead.
export const airconBookingPath = (countryCd) => {
    const market = getMarket(countryCd)
    return Object.values(market.hubs?.aircon ?? {})[0] ?? '/sg/aircon-servicing-singapore'
}

// Feature switch lookup; an unknown market (null) is treated as Singapore.
export const hasFeature = (market, key) => Boolean((market ?? MARKETS.sg).features?.[key])

export const marketHasVertical =(countryName, vertical) =>
    (getMarketByName(countryName)?.verticals ?? MARKETS.sg.verticals).includes(vertical)

// Site-wide "Services" menu entries for a market's public pages.
export const getNavHubLinks = (countryCd) => {
    const market = getMarket(countryCd)
    if (market.countryCd === 'sg') return null // SG keeps its own list in IndexNav
    const links = []
    market.cities.forEach((city) => {
        if (market.hubs.aircon[city.slug]) {
            links.push({ href: market.hubs.aircon[city.slug], label: `${t(market, 'acCategory')} ${city.name}` })
        }
    })
    market.cities.forEach((city) => {
        if (market.hubs.pt[city.slug]) {
            links.push({ href: market.hubs.pt[city.slug], label: `Personal Trainer ${city.name}` })
        }
    })
    return links
}

// Country switcher entries (nav + footer). Order is the menu order.
export const COUNTRY_SWITCHER = [
    { countryCd: 'sg', label: 'Singapore', href: '/sg' },
    { countryCd: 'ae', label: 'United Arab Emirates', href: '/ae' },
    { countryCd: 'my', label: 'Malaysia', href: '/my' },
]

// The country a visitor last browsed, so the global page at "/" can put it
// first. Per-browser convenience only - never a redirect, and losing it (private
// window, cleared cookies) just means no card is highlighted.
//
// A cookie, not localStorage: UserContext calls localStorage.clear() on every
// login-state check for logged-out visitors, which is exactly who sees "/".
const PREFERRED_MARKET_COOKIE = 'preferredMarket'

export const rememberMarket = (countryCd) => {
    try {
        if (!MARKETS[countryCd] || typeof document === 'undefined') return
        document.cookie = `${PREFERRED_MARKET_COOKIE}=${countryCd}; path=/; max-age=31536000; SameSite=Lax`
    } catch {}
}

export const getRememberedMarket = () => {
    try {
        if (typeof document === 'undefined') return null
        const match = document.cookie.match(new RegExp(`(?:^|; )${PREFERRED_MARKET_COOKIE}=([a-z]{2})`))
        return match && MARKETS[match[1]] ? match[1] : null
    } catch {
        return null
    }
}

// hreflang set shared by the four home pages: "/" (global, x-default), /sg,
// /ae and /my. Every page in the set emits the same list.
export const COUNTRY_HOME_ALTERNATES = [
    { hreflang: 'en-sg', path: '/sg' },
    { hreflang: 'en-ae', path: '/ae' },
    { hreflang: 'en-my', path: '/my' },
    { hreflang: 'x-default', path: '/' },
]

export const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
